import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { clientApi, getApiErrorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ClientLayout } from "@/components/layout/ClientLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { TwoFactorDialog } from "@/components/TwoFactorDialog";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import { CapabilityNotice } from "@/components/bse/BseUatForm";
import { useBseCapability } from "@/components/BseCapabilityStatus";
import { AlertTriangle, CheckCircle2, ArrowLeft, Landmark, Smartphone, Building2 } from "lucide-react";

export default function Invest() {
  const { schemeCode } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [scheme, setScheme] = useState(null);
  const [amount, setAmount] = useState("");
  const [mode, setMode] = useState("UPI");
  const [vpa, setVpa] = useState("");
  const [accountNo, setAccountNo] = useState("");
  const [demat, setDemat] = useState(false);
  const [uccModes, setUccModes] = useState({ physical: true, demat: true });
  const [ifsc, setIfsc] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [twoFa, setTwoFa] = useState({ open: false, order: null });
  const capability = useBseCapability("orders");
  const paymentCapability = useBseCapability("payments");

  useEffect(() => {
    clientApi.get(`/schemes/${schemeCode}`).then((r) => setScheme(r.data));
    clientApi.get("/bse/status").then((r) => {
      const active = (r.data.uccs || []).filter((item) => (item.status || "ACTIVE") === "ACTIVE");
      if (!active.length) return;
      const modes = {
        physical: active.some((item) => item.allows_physical || ["PHYSICAL", "BOTH"].includes(item.holding_mode)),
        demat: active.some((item) => item.allows_demat || ["DEMAT", "BOTH"].includes(item.holding_mode)),
      };
      setUccModes(modes);
      if (!modes.physical && modes.demat) setDemat(true);
    }).catch(() => {});
  }, [schemeCode]);

  const finalizeInvestment = async (purchase) => {
    const payment = await clientApi.post("/payments/initiate", {
      order_numbers: [purchase.order_number], total_amount: Number(amount),
      mode_of_payment: mode, account_number: accountNo, ifsc, vpa_id: mode === "UPI" ? vpa : undefined,
      neft_reference: mode === "NEFT" ? `NEFT${Date.now()}` : undefined,
    });
    const connected = payment.data.bse2 && payment.data.bse2.status !== "NOT_CONFIGURED";
    if (!connected) {
      throw new Error("BSE is not connected. Payment was not confirmed; check the order before retrying.");
    }
    const status = payment.data.bse2.status === "SUBMITTED" ? "AWAITING_CUSTOMER_PAYMENT" : "BSE_CONFIRMATION_PENDING";
    setResult({ order: purchase, payment: payment.data, status });
    if (payment.data.bse2.status === "SUBMITTED") {
      toast.success(payment.data.bse2.message || "Order accepted; complete payment through the BSE payment page.");
    } else {
      toast.warning(payment.data.bse2.message || "BSE payment confirmation is pending. Do not submit payment again until status is refreshed.");
    }
  };

  const handleInvest = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const purchase = await clientApi.post("/orders/purchase", {
        scheme_code: schemeCode, amount: Number(amount), bank_account_no: accountNo, demat,
      });
      if (purchase.data.requires_bse_auth || purchase.data.requires_2fa) {
        setTwoFa({ open: true, order: purchase.data });
      } else if (["SUBMITTING", "UNKNOWN"].includes(purchase.data.bse2?.status)) {
        setResult({ order: purchase.data, payment: null, status: "BSE_CONFIRMATION_PENDING" });
        toast("BSE confirmation is pending. Do not submit this order again.");
      } else {
        await finalizeInvestment(purchase.data);
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const handleVerify2FA = async () => {
    await clientApi.post(`/orders/${twoFa.order.order_number}/bse-auth/complete`);
    setTwoFa({ open: false, order: null });
    toast.success("Authentication verified. Preparing payment...");
    await finalizeInvestment(twoFa.order);
  };

  if (!scheme) return <ClientLayout><div className="py-16 text-center text-[var(--ink-muted)]">Loading...</div></ClientLayout>;

  if (result) {
    const exchangePending = result.status === "BSE_CONFIRMATION_PENDING";
    const ResultIcon = exchangePending ? AlertTriangle : CheckCircle2;
    return (
      <ClientLayout>
        <div className="mx-auto max-w-lg py-10 text-center">
          <ResultIcon className={`mx-auto h-16 w-16 ${exchangePending ? "text-amber-600" : "text-[var(--cta)]"}`} />
          <h1 className="mt-4 font-heading text-2xl text-[var(--brand)]">{exchangePending ? "BSE confirmation pending" : "Order accepted by BSE · payment pending"}</h1>
          <p className="mt-2 text-sm text-[var(--ink-muted)]">{scheme.scheme_name}</p>
          <div className="mt-6 space-y-2 rounded-xl bg-[var(--surface-2)] p-4 text-left text-sm">
            <div className="flex justify-between"><span className="text-[var(--ink-muted)]">Order Number</span><span className="font-mono-app font-medium" data-testid="invest-result-order-number">{result.order.order_number}</span></div>
            <div className="flex justify-between"><span className="text-[var(--ink-muted)]">Amount</span><span className="font-medium">{formatCurrency(amount)}</span></div>
            <div className="flex justify-between"><span className="text-[var(--ink-muted)]">Status</span><StatusBadge status={result.status} /></div>
            {result.payment?.bse2 && result.payment.bse2.status !== "NOT_CONFIGURED" && (
              <div className="flex justify-between gap-3">
                <span className="text-[var(--ink-muted)]">BSE Payment</span>
                <span className="text-right text-xs" data-testid="invest-result-bse-payment">
                  {result.payment.bse2.pg_url
                    ? <a href={result.payment.bse2.pg_url} target="_blank" rel="noreferrer" className="font-medium text-[var(--cta)] underline">Complete payment on BSE PG →</a>
                    : result.payment.bse2.message}
                </span>
              </div>
            )}
          </div>
          <p className="mt-4 text-xs text-[var(--ink-muted)]">The displayed status is sourced from BSE. Pending transactions must not be submitted again.</p>
          <div className="mt-6 flex flex-col justify-center gap-3 sm:flex-row">
            <Button variant="outline" className="rounded-full" onClick={() => navigate("/funds")} data-testid="invest-result-explore-more-button">Explore more funds</Button>
            <Button className="rounded-full bg-[var(--cta)]" onClick={() => navigate("/orders")} data-testid="invest-result-view-orders-button">View orders</Button>
          </div>
        </div>
      </ClientLayout>
    );
  }

  return (
    <ClientLayout>
      <button onClick={() => navigate(-1)} className="mb-4 flex items-center gap-1 text-sm text-[var(--ink-muted)]" data-testid="invest-back-button">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>
      <div className="mx-auto max-w-xl">
        <div className="mb-6 flex items-center gap-3">
          <Landmark className="h-6 w-6 text-[var(--cta)]" />
          <div>
            <h1 className="font-heading text-xl text-[var(--brand)]">{scheme.scheme_name}</h1>
            <p className="text-xs text-[var(--ink-muted)]">
              {scheme.amc_name} &middot; {scheme.transaction_metadata_verified && scheme.min_purchase_amt != null
                ? `Min ${formatCurrency(scheme.min_purchase_amt)}`
                : "Minimum confirmed by BSE at order time"}
            </p>
          </div>
        </div>

        <Card className="glass-card border-0">
          <CardHeader><CardTitle className="font-heading text-lg text-[var(--brand)]">Investment details</CardTitle></CardHeader>
          <CardContent>
            <form className="space-y-5" onSubmit={handleInvest}>
              <CapabilityNotice capability={capability} title="BSE purchase order" />
              <CapabilityNotice capability={paymentCapability} title="BSE payment initiation" />
              <div className="space-y-1.5">
                <Label>Amount (INR)</Label>
                <Input type="number" required min={scheme.transaction_metadata_verified ? scheme.min_purchase_amt : 1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={scheme.transaction_metadata_verified ? `Minimum ${scheme.min_purchase_amt}` : "Amount"} data-testid="invest-amount-input" />
                {!scheme.transaction_metadata_verified && <p className="text-xs text-[var(--ink-muted)]">Eligibility and minimum amount are validated by BSE before acceptance.</p>}
              </div>
              <div className="space-y-1.5">
                <Label>Holding mode</Label>
                <select className="w-full rounded-md border border-[var(--glass-border)] bg-white p-2 text-sm" value={demat ? "DEMAT" : "PHYSICAL"} onChange={(e) => setDemat(e.target.value === "DEMAT")}>
                  <option value="PHYSICAL" disabled={!uccModes.physical}>Physical / statement of account</option>
                  <option value="DEMAT" disabled={!uccModes.demat}>Demat</option>
                </select>
                <p className="text-xs text-[var(--ink-muted)]">Only modes enabled on your active BSE UCC are available.</p>
              </div>
              <div className="space-y-1.5">
                <Label>Bank account number</Label>
                <Input required value={accountNo} onChange={(e) => setAccountNo(e.target.value)} data-testid="invest-account-input" />
              </div>
              <div className="space-y-1.5">
                <Label>Bank IFSC</Label>
                <Input required maxLength={11} value={ifsc} onChange={(e) => setIfsc(e.target.value.toUpperCase())} data-testid="invest-ifsc-input" />
              </div>
              <div className="space-y-2">
                <Label>Payment mode</Label>
                <RadioGroup value={mode} onValueChange={setMode} className="grid grid-cols-1 gap-2 min-[420px]:grid-cols-3">
                  {[["UPI", Smartphone], ["DIRECT", Building2], ["NEFT", Building2]].map(([m, Icon]) => (
                    <label key={m} className={`flex cursor-pointer flex-col items-center gap-1 rounded-xl border p-3 text-xs ${mode === m ? "border-[var(--cta)] bg-[var(--surface-2)]" : "border-[var(--glass-border)]"}`}>
                      <RadioGroupItem value={m} className="sr-only" data-testid={`invest-mode-${m.toLowerCase()}`} />
                      <Icon className="h-4 w-4 text-[var(--brand)]" /> {m}
                    </label>
                  ))}
                </RadioGroup>
              </div>
              {mode === "UPI" && (
                <div className="space-y-1.5">
                  <Label>UPI VPA</Label>
                  <Input required placeholder="you@upi" value={vpa} onChange={(e) => setVpa(e.target.value)} data-testid="invest-vpa-input" />
                </div>
              )}
              <Button type="submit" disabled={loading || !capability?.enabled || !paymentCapability?.enabled} className="w-full rounded-full bg-[var(--cta)] hover:bg-[#087a56]" data-testid="invest-submit-button">
                {loading ? "Placing order..." : `Invest ${amount ? formatCurrency(amount) : ""}`}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <TwoFactorDialog
        open={twoFa.open}
        title="Confirm your investment (Subscription 2FA)"
        verificationLink={twoFa.order?.verification_link}
        onVerify={handleVerify2FA}
        onClose={() => setTwoFa({ open: false, order: null })}
      />
    </ClientLayout>
  );
}
